import React from 'react'
import Link from 'next/link'
import { Breadcrumb } from '../commons/Breadcrumb'
import { Faq } from '../commons/Faq'
import { MarketHead } from './MarketHead'
import { MarketBooking } from './MarketBooking'
import { MarketAirconHero } from './MarketAirconHero'
import { MarketPtHero } from './MarketPtHero'
import { BookButton, Checklist, InfoCards, LinkCards, PriceTable, Steps } from './ui'
import { airconHubPath, airconServiceLinks, buildContext, guideLinks, ptHubPath, ptTopicLinks, resolveAll } from './content'
import { articleNode, breadcrumbNode, faqNode, graph, webPageNode } from '../../utils/market-schema'
import { s3Static } from '../../utils/constants'
import { getPersonalTrainerPackages } from '../../data/PersonalTrainerPackages'
import { getPtModifiers } from '../../utils/pt-package-price'

// Article layout for the local-intent guides (landlord vs tenant, HP sizing,
// pantang exercise, Dubai Fitness Challenge...), under the same booking hero
// as the service pages: the aircon card (tiles, "More Options" picker) or the
// personal trainer hero (free consultation). The hero carries the guide's H1
// and photo; the article below keeps the intro, byline and content.
//
// Malay guides set content.lang = 'ms': copy, labels and inLanguage switch to
// Bahasa Melayu (pages/_document.js sets <html lang="ms"> for these routes).

const LABELS = {
    en: { faq: 'Questions people ask', related: 'Related', updated: 'Updated' },
    ms: { faq: 'Soalan lazim', related: 'Bacaan berkaitan', updated: 'Dikemas kini' },
}

const GuideSection = ({ section, countryCd }) => (
    <section className="mt-10">
        {section.title && <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">{section.title}</h2>}
        <div className="mt-4 space-y-4 text-slate-700 leading-relaxed">
            {(section.paragraphs ?? []).map((p, i) => <p key={i}>{p}</p>)}
        </div>
        {section.items && <div className="mt-4"><Checklist items={section.items} /></div>}
        {section.steps && <div className="mt-4"><Steps items={section.steps} /></div>}
        {section.cards && <div className="mt-4"><InfoCards items={section.cards} columns={2} /></div>}
        {section.table && (
            <div className="mt-4">
                <PriceTable countryCd={countryCd} columns={section.table.columns} rows={section.table.rows} caption={section.table.caption} moneyKeys={section.table.moneyKeys ?? []} />
            </div>
        )}
        {section.note && <p className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">{section.note}</p>}
    </section>
)

export const MarketGuidePage = ({ countryCd, citySlug, vertical, tables, requestCategorySettings, modifiedDate, content: raw }) => {
    const ctx = buildContext({ countryCd, citySlug, tables })
    const { market, city } = ctx
    const pkgs = getPersonalTrainerPackages(countryCd)
    const pt = (tier, sessions) => pkgs[tier].packages.find((p) => p.totalServices === sessions).perSessionPrice
    const content = resolveAll(raw, { ...ctx, pt, mods: getPtModifiers(countryCd), lowest: pt('Signature', 50) })
    const lang = content.lang || 'en'
    const L = LABELS[lang]
    const hubPath = vertical === 'pt' ? ptHubPath(market, citySlug) : airconHubPath(market, citySlug)
    const path = `${hubPath}/${content.slug}`
    const inLanguage = lang === 'ms' ? 'ms-MY' : market.locale

    const jsonLd = graph(
        webPageNode({ market, path, title: content.title, description: content.description, dateModified: modifiedDate, inLanguage }),
        breadcrumbNode(path, content.h1),
        articleNode({
            market,
            path,
            headline: content.h1,
            description: content.description,
            datePublished: content.datePublished,
            dateModified: modifiedDate,
            inLanguage,
            image: content.image ? s3Static + content.image : undefined,
        }),
        content.faqs?.length ? faqNode(path, content.faqs) : null,
    )

    const related = [
        ...(content.relatedLinks ?? []),
        ...guideLinks(market, citySlug, vertical, [content.slug]),
    ]
    const serviceLinks = vertical === 'pt' ? ptTopicLinks(market, citySlug).slice(0, 3) : airconServiceLinks(market, citySlug).slice(0, 3)

    return (
        <>
            <MarketHead
                market={market}
                path={path}
                title={content.title}
                description={content.description}
                jsonLd={jsonLd}
                alternates={content.alternates ?? []}
                ogImageAlt={content.imageAlt}
                contentLanguage={lang === 'ms' ? 'ms-my' : undefined}
            />
            <MarketBooking market={market} city={city} requestType={vertical === 'pt' ? 'Fitness Trainer' : 'Aircon Services'} requestCategorySettings={requestCategorySettings} inlineForm={vertical === 'pt'}>
                {vertical === 'pt' ? (
                    <MarketPtHero
                        market={market}
                        city={city}
                        title={content.h1}
                        heroCopy={content.lead}
                        image={content.image || 'personal-trainer/personal-trainer.jpg'}
                    />
                ) : (
                    <MarketAirconHero
                        market={market}
                        tables={tables}
                        panel={{ type: 'servicing', defaultMount: 'Wall' }}
                        title={content.h1}
                        subtitle={lang === 'ms' ? `Harga jelas di seluruh ${city.name}, termasuk pengangkutan` : `Upfront prices across ${city.name}, travel included`}
                        image={content.image || 'blog/living/repairman-doing-air-conditioner-service.jpg'}
                        imageAlt={content.imageAlt}
                        pills={lang === 'ms' ? ['Harga jelas', 'Termasuk pengangkutan'] : ['Upfront prices', 'Travel included']}
                    />
                )}
                <Breadcrumb pageTitle={content.h1} jsonLd={false} />
                <article className="mx-auto max-w-3xl px-4 pb-12" lang={lang === 'ms' ? 'ms' : undefined}>
                    <header className="pt-4">
                        {content.eyebrow && <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">{content.eyebrow}</p>}
                        {/* The H1 is in the hero; the PT hero also shows the lead. */}
                        {vertical !== 'pt' && <p className="mt-2 text-lg text-slate-600 leading-relaxed">{content.lead}</p>}
                        <p className="mt-3 text-sm text-slate-500">
                            StringsSG · {L.updated}{' '}
                            <time dateTime={modifiedDate}>{new Date(modifiedDate).toLocaleDateString(lang === 'ms' ? 'ms-MY' : market.locale, { day: 'numeric', month: 'long', year: 'numeric' })}</time>
                        </p>
                    </header>

                    {content.summary && (
                        <aside className="mt-8 rounded-2xl border border-orange-200 bg-orange-50 p-5">
                            <p className="font-bold text-slate-900">{content.summary.title}</p>
                            <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-700">
                                {content.summary.items.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </aside>
                    )}

                    {(content.sections ?? []).map((section, i) => (
                        <GuideSection key={i} section={section} countryCd={countryCd} />
                    ))}

                    {content.cta && (
                        <div className="mt-12 rounded-3xl bg-slate-900 p-8 text-white">
                            <p className="text-xl font-bold">{content.cta.title}</p>
                            {content.cta.body && <p className="mt-2 text-slate-300">{content.cta.body}</p>}
                            <div className="mt-5 flex flex-wrap gap-3">
                                {content.cta.href && (
                                    <Link href={content.cta.href} className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-bold text-white transition hover:bg-orange-600">
                                        {content.cta.label}
                                    </Link>
                                )}
                                {content.cta.bookSummary && (
                                    <BookButton tile={{ summary: content.cta.bookSummary }} variant="secondary">
                                        {content.cta.bookLabel || (lang === 'ms' ? 'Tempah sekarang' : 'Book now')}
                                    </BookButton>
                                )}
                            </div>
                        </div>
                    )}

                    {content.faqs?.length > 0 && (
                        <div className="mt-12">
                            <Faq questionsAnswers={content.faqs} title={content.faqTitle || L.faq} showSearch={false} jsonLd={false} />
                        </div>
                    )}

                    {related.length > 0 && (
                        <section className="mt-12">
                            <h2 className="mb-4 text-xl font-extrabold text-slate-900">{L.related}</h2>
                            <LinkCards links={related} columns={2} />
                        </section>
                    )}
                    {lang !== 'ms' && serviceLinks.length > 0 && (
                        <section className="mt-8">
                            <LinkCards links={serviceLinks} columns={3} />
                        </section>
                    )}
                </article>
            </MarketBooking>
        </>
    )
}
